import {
  IconBrandTelegram,
  IconCircleCheck,
  IconCircleX,
  IconClockHour3,
  IconShoppingBag,
  IconShoppingBagCheck,
  IconShoppingBagEdit,
  IconShoppingBagExclamation,
  IconShoppingBagX,
  IconWritingSign,
} from '@tabler/icons-react';
import { EnrollmentStatus } from '@zorro/clients';
import { Pill, PillColor } from '@zorro/design-system/v2/Pill';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import {
  CoverageEndedIconNoBackground,
  IconNotApplicable,
} from '@zorro/shared/assets';

import { getEnrollmentStatusView } from './EnrollmentStatusUtils';
import { useEnrollmentStatusViewer } from './useEnrollmentStatusViewer';

type Props = {
  enrollmentStatus?: EnrollmentStatus;
  shouldDisableTooltip?: boolean;
  isEmployeeView?: boolean;
  className?: string;
};

type StatusVisual = {
  color: PillColor;
  Icon: React.ComponentType<Record<string, never>>;
};

const STATUS_VISUALS: Record<EnrollmentStatus, StatusVisual> = {
  [EnrollmentStatus.PENDING_ELECTION_WINDOW]: {
    color: 'gray',
    Icon: IconShoppingBag,
  },
  [EnrollmentStatus.ELECTION_ACTIVE]: {
    color: 'orange',
    Icon: IconShoppingBagEdit,
  },
  [EnrollmentStatus.ELECTION_ACTIVE_HAS_NOT_STARTED]: {
    color: 'yellow',
    Icon: IconShoppingBagExclamation,
  },
  [EnrollmentStatus.ELECTION_ACTIVE_STARTED]: {
    color: 'orange',
    Icon: IconShoppingBagEdit,
  },
  [EnrollmentStatus.ELECTION_SUBMITTED]: {
    color: 'indigo',
    Icon: IconShoppingBagCheck,
  },
  [EnrollmentStatus.WAIVED_ELECTION]: {
    color: 'steel',
    Icon: IconShoppingBagX,
  },
  [EnrollmentStatus.DEADLINE_PASSED]: {
    color: 'red',
    Icon: IconClockHour3,
  },
  [EnrollmentStatus.ENROLLMENT_CONFIRMED]: {
    color: 'green',
    Icon: IconWritingSign,
  },
  [EnrollmentStatus.ENROLLMENT_IN_PROCESS]: {
    color: 'blue',
    Icon: IconBrandTelegram,
  },
  [EnrollmentStatus.COVERAGE_ENDED]: {
    color: 'steel',
    Icon: CoverageEndedIconNoBackground,
  },
  [EnrollmentStatus.ACTIVE_COVERAGE]: {
    color: 'green',
    Icon: IconCircleCheck,
  },
  [EnrollmentStatus.WAIVED_COVERAGE]: {
    color: 'steel',
    Icon: IconCircleX,
  },
  [EnrollmentStatus.NO_ENROLLMENTS]: {
    color: 'pink',
    Icon: IconNotApplicable,
  },
};

export function EnrollmentStatusBadge({
  enrollmentStatus = EnrollmentStatus.NO_ENROLLMENTS,
  shouldDisableTooltip,
  isEmployeeView,
  className,
}: Props) {
  const viewer = useEnrollmentStatusViewer(isEmployeeView);
  const { label, tooltipLabel } = getEnrollmentStatusView(
    enrollmentStatus,
    viewer
  );
  const { color, Icon } = STATUS_VISUALS[enrollmentStatus];

  return (
    <Tooltip
      label={tooltipLabel}
      position="top"
      disabled={shouldDisableTooltip || !tooltipLabel}
    >
      <Pill
        variant="with-icon"
        color={color}
        leftSection={<Icon />}
        className={className}
      >
        {label}
      </Pill>
    </Tooltip>
  );
}
